import { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiButtonGroup,
  MiDetailFields,
  MiPageHeader,
} from "@miplatform/components";
import { BUTTON_VARIANTS, ICONS_WEB } from "@miplatform/constants";
import { InvoiceStatus } from "@miplatform/types";
import * as mdi from "@mdi/js";
import { Box, Divider, Slide, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import {
  INVOICING_ROUTES,
  RECONCILIATION_STEPS,
} from "../../accountingConstants";
import { useReconcileContext } from "../ReconcileContext";
import { AccountingInvoice } from "./AccountingInvoice";
import { AccountingVariance } from "./AccountingVariance";

export const ReconcileInvoiceSection = () => {
  const {
    order,
    invoices,
    variances,
    setInvoices,
    activeStep,
    notesEdit,
    viewMode,
    invoicesModule,
  } = useReconcileContext();

  const theme = useTheme();
  const { classes } = useStyles();

  const { handleSaveNotes } = invoicesModule;

  const [selectedInvoiceView, setSelectedInvoiceView] = useState(
    viewMode === INVOICING_ROUTES.INVOICED
      ? InvoiceStatus.Invoiced
      : InvoiceStatus.Draft
  );

  const hasNonPendingInvoices = useMemo(
    () =>
      invoices.some(
        (invoice) => invoice.status && invoice.status !== InvoiceStatus.Draft
      ),
    [invoices]
  );

  const groupedInvoices = useMemo(() => {
    if (!hasNonPendingInvoices) {
      return invoices;
    }

    return invoices.filter((invoice) => invoice.status === selectedInvoiceView);
  }, [invoices, selectedInvoiceView]);
  const persistedInvoicesCount = useMemo(
    () => groupedInvoices.filter((invoice) => !invoice?.isNew).length,
    [groupedInvoices]
  );

  const handleAddNewInvoice = () => {
    if (!order?.id) return;

    const newInvoice = {
      orderId: order.id,
      name: "",
      status: InvoiceStatus.Draft,
      invoiceItems: [],
      total: 0,
      isNew: true,
    };

    setInvoices((prevInvoices) => [...prevInvoices, newInvoice]);
  };

  const notesFields = [
    {
      label: "General Notes",
      name: "notes",
      multiline: true,
      minRows: 2,
      maxRows: 4,
    },
    {
      label: "Variance Notes",
      name: "varianceNotes",
      multiline: true,
      minRows: 2,
      maxRows: 4,
    },
  ];

  const ANIMATION_DURATION = 200;
  const INVOICE_BUTTON_WIDTH = 120;

  const hasNewInvoice = useMemo(
    () => invoices.some((inv) => inv?.isNew),
    [invoices]
  );

  const newInvoiceRef = useRef(null);

  useEffect(() => {
    if (hasNewInvoice && newInvoiceRef.current) {
      newInvoiceRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [hasNewInvoice]);

  const showAddInvoiceButton =
    selectedInvoiceView === InvoiceStatus.Draft &&
    viewMode !== INVOICING_ROUTES.INVOICED;

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      minWidth="400px"
      backgroundColor="background.paper"
      sx={{ overflow: "hidden" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: 0,
          gap: 4,
        }}
      >
        {viewMode === INVOICING_ROUTES.PRE_BILL && (
          <Box sx={{ flexShrink: 0 }}>
            <MiAlert
              title="Add items to pre-bill invoice"
              description="In order to pre-bill an order, the invoice items will need to be added and appropriate amounts should be added from payment information to their respective invoices."
              sx={{ margin: theme.spacing(4, 5, 0, 5) }}
            />
          </Box>
        )}
        <Box sx={{ flexShrink: 0 }}>
          <MiPageHeader
            title="Invoices"
            rightArea={
              activeStep !== RECONCILIATION_STEPS.REVIEW_SUBMIT && (
                <Box display="flex" alignItems="center" gap={2}>
                  {hasNonPendingInvoices && (
                    <Box
                      sx={{
                        transition: `transform ${ANIMATION_DURATION}ms ease-in-out`,
                        transform: showAddInvoiceButton
                          ? "translateX(0)"
                          : `translateX(${INVOICE_BUTTON_WIDTH}px)`,
                      }}
                    >
                      <MiButtonGroup
                        data={[
                          {
                            label: "Pending",
                            value: InvoiceStatus.Draft,
                          },
                          {
                            label: "Invoiced",
                            value: InvoiceStatus.Invoiced,
                          },
                          {
                            label: "Paid",
                            value: InvoiceStatus.Paid,
                          },
                          {
                            label: "Voided",
                            value: InvoiceStatus.Voided,
                          },
                        ]}
                        selected={selectedInvoiceView}
                        handleClick={({ value }) => {
                          setSelectedInvoiceView(value);
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      minWidth: INVOICE_BUTTON_WIDTH,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Slide
                      direction="left"
                      in={showAddInvoiceButton}
                      timeout={ANIMATION_DURATION}
                      mountOnEnter
                      unmountOnExit
                    >
                      <Box>
                        <MiButton
                          title="Add Invoice"
                          startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                          variant={BUTTON_VARIANTS.OUTLINED}
                          disabled={hasNewInvoice}
                          className={classes.actionButton}
                          onClick={handleAddNewInvoice}
                        />
                      </Box>
                    </Slide>
                  </Box>
                </Box>
              )
            }
          />
        </Box>
        {groupedInvoices.length === 0 ? (
          <Box mx={4} my={2} textAlign="center">
            <Typography variant="h5" color="text.secondary">
              No invoices for this group.
            </Typography>
          </Box>
        ) : (
          groupedInvoices.map((invoice, index) => (
            <Box
              key={`invoice-${index}`}
              ref={invoice.isNew ? newInvoiceRef : null}
            >
              <AccountingInvoice
                invoice={invoice}
                invoicesCount={persistedInvoicesCount}
                invoiceHeaderNumber={index + 1}
              />
            </Box>
          ))
        )}
        <Divider flexItem />
        {variances.length > 0 && (
          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="h5" fontWeight="bold" pl={4}>
              <span>&nbsp;</span>
              Previous Order Outstanding Variances
            </Typography>
          </Box>
        )}
        {variances.map((variance, index) => (
          <AccountingVariance
            key={variance?.originatingInvoice.id || index}
            variance={variance}
          />
        ))}
        <Box
          sx={{
            height: "fit-content",
            maxHeight: "fit-content",
          }}
        >
          <MiPageHeader
            title="Notes"
            rightArea={
              notesEdit && (
                <MiButton
                  title="Save Notes"
                  variant={BUTTON_VARIANTS.TEXT}
                  size="small"
                  disabled={!notesEdit.isDirty()}
                  onClick={handleSaveNotes}
                />
              )
            }
          />

          <Box mx={4} mb={4}>
            <MiDetailFields detailFields={notesFields} edit={notesEdit} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  actionButton: {
    borderWidth: "2px !important",
    borderColor: `${theme.palette.primary.main} !important`,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    "&:hover": {
      borderWidth: "2px !important",
    },
  },
}));
